/**
 * BFCache (Back/Forward Cache) 相关工具函数
 * 用于解决 iPhone 系统侧滑后退/前进导致页面刷新的问题
 */

/**
 * 监听 bfcache 事件并执行相应处理
 * @param {Function} onPageShow - 页面从缓存恢复时的回调
 * @param {Function} onPageHide - 页面隐藏时的回调
 */
export function setupBfcacheListeners(onPageShow, onPageHide) {
  // pageshow 事件：页面显示或从缓存恢复时触发
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // persisted 为 true 表示页面从 bfcache 恢复
      console.log('Page restored from bfcache');
      onPageShow && onPageShow(event);
    }
  });

  // pagehide 事件：页面隐藏或被缓存时触发
  window.addEventListener('pagehide', (event) => {
    if (event.persisted) {
      // persisted 为 true 表示页面被缓存到 bfcache
      console.log('Page saved to bfcache');
      onPageHide && onPageHide(event);
    }
  });

  // visibilitychange 事件：页面可见性变化时触发
  window.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('Page is hidden');
    } else {
      console.log('Page is visible');
    }
  });
}

/**
 * 禁用某些可能导致 bfcache 无法工作的特性
 * 某些 JavaScript 特性会阻止页面被缓存到 bfcache
 */
export function optimizeForBfcache() {
  // 不要使用 unload/beforeunload 事件，这会阻止 bfcache
  // window.addEventListener('unload', () => { });  // ❌ 避免

  // 不要使用 unbeforeunload，除非必要
  // window.addEventListener('beforeunload', () => { });  // ⚠️ 谨慎使用

  // 如果必须监听 beforeunload，确保不会阻止 bfcache
  window.addEventListener('beforeunload', (event) => {
    // 只在真正需要时执行，例如有未保存的更改
    // 不要无条件地设置 returnValue
  });
}

/**
 * 检测当前页面是否被浏览器后退/前进触发
 * @returns {boolean}
 */
export function isBackForwardNavigation() {
  // 使用 Performance API 检查导航类型
  if (window.performance && window.performance.navigation) {
    // TYPE_BACK_FORWARD = 2
    return window.performance.navigation.type === 2;
  }

  // 使用 PerformanceNavigationTiming（新 API）
  if (window.performance && window.performance.getEntriesByType) {
    const navigation = window.performance.getEntriesByType('navigation')[0];
    if (navigation) {
      return navigation.type === 'back_forward';
    }
  }

  return false;
}

/**
 * 修复 iPhone 上的过渡动画问题
 * 在 bfcache 恢复时禁用动画，避免闪烁
 */
export function fixIphoneTransitionOnBfcache() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (!isIOS) return;

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      // 从 bfcache 恢复，禁用所有过渡动画
      document.documentElement.classList.add('bfcache-restore');
      
      // 在动画完成后移除类名
      setTimeout(() => {
        document.documentElement.classList.remove('bfcache-restore');
      }, 50);
    }
  });
}

/**
 * 获取当前页面的滚动位置
 * 用于在 bfcache 恢复时保持滚动位置
 */
export function getScrollPosition() {
  return {
    x: window.scrollX || window.pageXOffset,
    y: window.scrollY || window.pageYOffset
  };
}

/**
 * 恢复页面的滚动位置
 */
export function restoreScrollPosition(position) {
  if (position) {
    window.scrollTo(position.x, position.y);
  }
}
